










import {
    defineComponent,
    computed,
    ref,
    onBeforeUnmount,
    onBeforeMount,
} from '@vue/composition-api';
import { RouteRecordPublic } from 'vue-router';
import { getConfig } from '@superbet-group/web.lib.config';
import { SdsAlert } from '@superbet-group/web.lib.ui-components';
import { getPlatformParameter } from '@src/federations/shared/cms/types/common';
import { RouteName } from '@src/app/router/constants/RouteName';
import { getCmsUrl } from '@core/services/cms/utils';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { GamingRoutes } from '@superbet-group/web.types';
import { useVueRouter } from '@superbet-group/web.lib.vue-utilities';

interface ServiceMessage {
    message: string;
    locations: string[];
}

interface SSEEventData {
    serviceMessages: ServiceMessage[];
}

// logic on how to display service messages according to route can be found here:
// https://www.notion.so/superbet/Service-Messages-per-vertical-screen-120032f852c5809885d8dc08627133d3?pvs=4
const routeMapping = [
    { routeName: RouteName.App.Home, returnValue: 'sportsAppHome' },
    { routeName: GamingRoutes.MyCasinoHome, returnValue: 'casinoAppHome' },
    { routeName: GamingRoutes.MyCasinoLive, returnValue: 'casinoLive' },
    { routeName: GamingRoutes.GamesSuperTypeLiveCasino, returnValue: 'casinoLive' },
    { routeName: GamingRoutes.GamesSuperTypeDice, returnValue: 'casinoDice' },
    { routeName: GamingRoutes.JackpotHub, returnValue: 'casinoJackpotHub' },
    { routeName: RouteName.Account.Withdrawal, returnValue: 'withdrawal' },
    { routeName: RouteName.Account.Deposit, returnValue: 'deposit' },
];

const sportsRouteMapping = Object.values(RouteName.Sport).map((route) => ({
    routeName: route,
    returnValue: 'sportsApp',
}));
routeMapping.push(...sportsRouteMapping);

const gamingRouteMapping = Object.values(GamingRoutes).map((route) => ({
    routeName: route,
    returnValue: 'casinoApp',
}));
routeMapping.push(...gamingRouteMapping);

export default defineComponent({
    name: 'ServiceMessageContainer',
    setup() {
        const appRoutes = ref<RouteRecordPublic[]>([]);
        const config = getConfig();
        const country = config.countrySpecific.country;
        const locale = config.appLocale.locale;
        const platform = getPlatformParameter();
        const sseEventSource = ref<EventSource | null>(null);
        const isLiveServiceMessageEndpointEnabled = ref<boolean>(false);
        const allServiceMessages = ref<ServiceMessage[]>([]);
        const { route: currentRoute } = useVueRouter();

        const configServiceMessage = config.applicationStatus.serviceMessage;

        // eslint-disable-next-line promise/always-return
        import('@src/app/router').then(({ default: router }) => {
            appRoutes.value = router.getRoutes();
        }).catch((error) => {
            console.error('Error loading router module:', error);
        });

        const currentLocation = computed(() => {
            const matchedRoute = routeMapping.find((mapping) =>
                appRoutes.value.some((route) => route.name === mapping.routeName && route.name === currentRoute.value.name),
            );

            return matchedRoute?.returnValue ?? 'all';
        });

        const liveServiceMessage = computed(() => {
            if (!allServiceMessages.value.length) {
                return null;
            }

            const relevantMessages = allServiceMessages.value.filter((msg) =>
                msg.locations && (
                    msg.locations.includes('all')
                    || msg.locations.includes(currentLocation.value)
                ),
            );

            return relevantMessages[0]?.message || null;
        });

        const serviceMessage = computed(() => {
            return isLiveServiceMessageEndpointEnabled.value && liveServiceMessage.value
                ? liveServiceMessage.value
                : configServiceMessage;
        });

        const connectToSSE = () => {
            try {
                const sseLiveCmsEndpoint = getCmsUrl({
                    path: '/live',
                    queryParams: {
                        locale,
                        target: country,
                        platform,
                    },
                });
                sseEventSource.value?.close();

                sseEventSource.value = new EventSource(sseLiveCmsEndpoint);

                sseEventSource.value.addEventListener('message', (event: MessageEvent) => {
                    try {
                        const data: SSEEventData = JSON.parse(event.data);

                        if (data.serviceMessages && Array.isArray(data.serviceMessages) && data.serviceMessages.length > 0) {
                            allServiceMessages.value = data.serviceMessages;
                        } else {
                            allServiceMessages.value = [];
                        }
                    } catch (parseError) {
                        console.error('Error parsing SSE message:', parseError);
                    }
                });

                sseEventSource.value.addEventListener('error', () => {
                    if (sseEventSource.value) {
                        disconnectFromSSE();
                    }

                    setTimeout(connectToSSE, 5000);
                });
            } catch (error) {
                console.error('Failed to establish SSE connection:', error);
            }
        };

        const disconnectFromSSE = () => {
            sseEventSource.value?.close();
            sseEventSource.value = null;
        };

        let featureFlagUnsubscribe: (() => void) | null = null;

        onBeforeMount(() => {
            featureFlagUnsubscribe = featureFlagService.subscribeToFeatureChanges(
                'web.enable-live-service-messages',
                (isEnabled) => {
                    isLiveServiceMessageEndpointEnabled.value = isEnabled;

                    if (isEnabled) {
                        connectToSSE();
                    } else if (sseEventSource.value) {
                        disconnectFromSSE();
                    }
                },
                false,
            );
        });

        onBeforeUnmount(() => {
            if (sseEventSource.value) {
                disconnectFromSSE();
            }

            if (featureFlagUnsubscribe) {
                featureFlagUnsubscribe();
            }
        });

        return {
            serviceMessage,
        };
    },
    components: {
        SdsAlert,
    },
});
